import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, axiosClient } from '../auth-lib';
import { Button, Card, Input, Skeleton, EmptyState, useToast } from '../design-system';
import { TotpScanStep, TotpRecoveryCodesStep } from './TotpSetupSteps';
import { sanitizeOtp, isCompleteOtp, OTP_LENGTH } from '../lib/otpInput';
import './SecurityPanel.css';

// Account security, self-service only: the caller's own active sessions
// (one per device holding a live refresh token) and password change.
// Duplicated in the admin client — keep the two copies in step.

const emptyPwForm = { currentPassword: '', newPassword: '', confirm: '', otp: '' };

// "Mozilla/5.0 (Macintosh; …) Chrome/120…" → "Chrome · Mac" — best-effort.
const describeAgent = (ua = '') => {
  const browser = ua.includes('Edg/')
    ? 'Edge'
    : ua.includes('Chrome/')
      ? 'Chrome'
      : ua.includes('Firefox/')
        ? 'Firefox'
        : ua.includes('Safari/')
          ? 'Safari'
          : 'Browser';
  const os = ua.includes('Windows')
    ? 'Windows'
    : ua.includes('Macintosh')
      ? 'Mac'
      : ua.includes('Android')
        ? 'Android'
        : ua.includes('iPhone') || ua.includes('iPad')
          ? 'iOS'
          : ua.includes('Linux')
            ? 'Linux'
            : '';
  return os ? `${browser} · ${os}` : browser;
};

function SecurityPanel() {
  const { logout, startTotpSetup, confirmTotpSetup } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  // 'idle' — showing whatever the account currently has;
  // 'scan'/'recovery' — mid-enrolment; 'disable' — the confirm form.
  const [totp, setTotp] = useState(null);
  const [totpStep, setTotpStep] = useState('idle');
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpCodes, setTotpCodes] = useState([]);
  const [totpBusy, setTotpBusy] = useState(false);
  const [totpError, setTotpError] = useState('');
  const [disableForm, setDisableForm] = useState({ password: '', code: '' });
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState(null);
  const [pwForm, setPwForm] = useState(emptyPwForm);
  const [pwError, setPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      // withCredentials: the httpOnly session cookie lets the server mark which
      // row is "this device" (isCurrent) — without it every row looks foreign.
      const res = await axiosClient.get('/auth/sessions', { withCredentials: true });
      setSessions(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchTotp = async () => {
    try {
      const res = await axiosClient.get('/auth/totp/status');
      setTotp(res.data);
    } catch {
      // A panel that can't read its 2FA status should still render sessions and
      // the password form rather than blanking the page.
      setTotp(null);
    }
  };

  useEffect(() => {
    fetchSessions();
    fetchTotp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Two-factor authentication ──────────────────────────────────────────────
  const beginTotpSetup = async () => {
    setTotpError('');
    setTotpBusy(true);
    try {
      setTotpSetup(await startTotpSetup());
      setTotpStep('scan');
    } catch (err) {
      toast.danger(err.error || 'Could not start setup');
    } finally {
      setTotpBusy(false);
    }
  };

  const finishTotpSetup = async (code) => {
    setTotpError('');
    setTotpBusy(true);
    try {
      const res = await confirmTotpSetup({ code });
      setTotpCodes(res.recoveryCodes || []);
      setTotpStep('recovery');
      await fetchTotp();
    } catch (err) {
      setTotpError(err.error || 'That code was not accepted.');
    } finally {
      setTotpBusy(false);
    }
  };

  const submitDisable = async (e) => {
    e.preventDefault();
    setTotpError('');
    setTotpBusy(true);
    try {
      await axiosClient.post('/auth/totp/disable', disableForm);
      toast.success('Two-factor authentication turned off');
      setDisableForm({ password: '', code: '' });
      setTotpStep('idle');
      await fetchTotp();
    } catch (err) {
      setTotpError(err.response?.data?.error || 'Could not turn it off');
    } finally {
      setTotpBusy(false);
    }
  };

  const regenerateCodes = async (e) => {
    e.preventDefault();
    setTotpError('');
    setTotpBusy(true);
    try {
      const res = await axiosClient.post('/auth/totp/recovery-codes', disableForm);
      setTotpCodes(res.data.recoveryCodes || []);
      setDisableForm({ password: '', code: '' });
      setTotpStep('recovery');
      await fetchTotp();
    } catch (err) {
      setTotpError(err.response?.data?.error || 'Could not generate new codes');
    } finally {
      setTotpBusy(false);
    }
  };

  const revokeOne = async (s) => {
    setRevokingId(s.id);
    try {
      await axiosClient.delete(`/auth/sessions/${s.id}`);
      // Revoking the device you're on only kills the refresh token — the access
      // token stays valid for up to 15m. Finish the job locally, same as
      // "Sign out everywhere", so revoking your own session logs you out now.
      if (s.isCurrent) {
        toast.success('Signed out of this device — logging you out');
        await logout();
        navigate('/login');
        return;
      }
      setSessions((prev) => prev.filter((x) => x.id !== s.id));
      toast.success('Session revoked');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to revoke session');
    } finally {
      setRevokingId(null);
    }
  };

  const revokeAll = async () => {
    try {
      await axiosClient.post('/auth/sessions/revoke-all');
      toast.success('Signed out everywhere — logging you out');
      await logout();
      navigate('/login');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to revoke sessions');
    }
  };

  // Changing a password now needs the mailbox as well as the old password, so
  // a stolen session or a shoulder-surfed password isn't enough on its own.
  const sendPwOtp = async () => {
    setPwError('');
    setOtpSending(true);
    try {
      const res = await axiosClient.post('/auth/change-password/request-otp');
      setOtpSent(true);
      toast.success(res.data?.message || 'Code sent to your email');
    } catch (err) {
      setPwError(err.response?.data?.error || 'Could not send the code');
    } finally {
      setOtpSending(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwError('');
    if (pwForm.newPassword !== pwForm.confirm) {
      setPwError('New passwords do not match');
      return;
    }
    setPwSaving(true);
    try {
      await axiosClient.post('/auth/change-password', {
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
        otp: pwForm.otp,
      });
      toast.success('Password changed — sign in again with the new one');
      // The server revoked every session; finish the job locally.
      await logout();
      navigate('/login');
    } catch (err) {
      setPwError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPwSaving(false);
    }
  };

  // The proof both destructive 2FA actions demand: the password AND a current
  // code. A stolen session must not be able to strip the control that would
  // have stopped it, and the password alone is exactly what 2FA assumes may
  // already be compromised.
  const stepUpFields = (
    <>
      <Input
        label="Current password"
        id="totp-stepup-pw"
        type="password"
        value={disableForm.password}
        onChange={(e) => setDisableForm((f) => ({ ...f, password: e.target.value }))}
        disabled={totpBusy}
        autoComplete="current-password"
      />
      {/* A recovery code is deliberately NOT accepted here — the server refuses
          one for regenerating, and disabling should need the app in hand. So
          this is the strict six-digit filter, not the login box's looser one. */}
      <Input
        label="Code from your authenticator app"
        id="totp-stepup-code"
        inputMode="numeric"
        placeholder="123456"
        value={disableForm.code}
        onChange={(e) => setDisableForm((f) => ({ ...f, code: sanitizeOtp(e.target.value) }))}
        maxLength={OTP_LENGTH}
        disabled={totpBusy}
        autoComplete="one-time-code"
      />
    </>
  );

  return (
    <div className="security-panel">
      <Card className="security-card">
        <Card.Header
          title="Two-Factor Authentication"
          subtitle={
            totp?.enabled
              ? 'On — signing in needs a code from your authenticator app'
              : 'Off — your password alone signs you in'
          }
        />

        {totpStep === 'scan' ? (
          <TotpScanStep
            setup={totpSetup}
            onConfirm={finishTotpSetup}
            loading={totpBusy}
            error={totpError}
            onCancel={() => {
              setTotpStep('idle');
              setTotpError('');
            }}
          />
        ) : totpStep === 'recovery' ? (
          <TotpRecoveryCodesStep
            codes={totpCodes}
            doneLabel="Done"
            onDone={() => {
              setTotpCodes([]);
              setTotpStep('idle');
            }}
          />
        ) : totpStep === 'disable' || totpStep === 'regenerate' ? (
          <form
            className="security-pw-form"
            onSubmit={totpStep === 'disable' ? submitDisable : regenerateCodes}
          >
            {totpError && (
              <div className="security-pw-error" role="alert">
                {totpError}
              </div>
            )}
            {stepUpFields}
            <div className="security-totp-actions">
              <Button
                type="submit"
                variant={totpStep === 'disable' ? 'danger' : 'primary'}
                loading={totpBusy}
                disabled={!disableForm.password || !isCompleteOtp(disableForm.code)}
              >
                {totpStep === 'disable' ? 'Turn off' : 'Generate new codes'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={totpBusy}
                onClick={() => {
                  setTotpStep('idle');
                  setTotpError('');
                  setDisableForm({ password: '', code: '' });
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : totp?.enabled ? (
          <div className="security-totp-summary">
            <p className="session-meta">
              Turned on {totp.activatedAt ? new Date(totp.activatedAt).toLocaleDateString() : ''} ·{' '}
              {totp.recoveryCodesRemaining} recovery{' '}
              {totp.recoveryCodesRemaining === 1 ? 'code' : 'codes'} left
              {totp.required && ' · required by your role'}
            </p>
            {/* Running out is the lockout this feature exists to prevent, so
                say so before it happens rather than after. */}
            {totp.recoveryCodesRemaining <= 2 && (
              <div className="security-pw-error" role="alert">
                You are nearly out of recovery codes. Generate a new set so a lost phone doesn't
                lock you out.
              </div>
            )}
            <div className="security-totp-actions">
              <Button variant="secondary" size="sm" onClick={() => setTotpStep('regenerate')}>
                New recovery codes
              </Button>
              {/* Hidden when the role requires 2FA — the server refuses it too,
                  so this only avoids offering a button that cannot work. */}
              {!totp.required && (
                <Button variant="secondary" size="sm" onClick={() => setTotpStep('disable')}>
                  Turn off
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="security-totp-summary">
            <p className="session-meta">
              An authenticator app generates a 6-digit code that changes every 30 seconds. With it
              on, a stolen password is not enough to reach your firm's data.
            </p>
            <div className="security-totp-actions">
              <Button variant="primary" size="sm" loading={totpBusy} onClick={beginTotpSetup}>
                Set up authenticator app
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="security-card">
        <Card.Header
          title="Active Sessions"
          subtitle="Every device currently signed in to your account"
        />
        {loading ? (
          <Skeleton height={80} />
        ) : sessions.length === 0 ? (
          <EmptyState icon="🖥️" title="No active sessions" description="Sessions appear when you sign in." />
        ) : (
          <>
            <div className="session-list">
              {sessions.map((s) => (
                <div key={s.id} className="session-row">
                  <div className="session-info">
                    <span className="session-device">
                      {describeAgent(s.userAgent)}
                      {s.isCurrent && <span className="session-current-badge">This device</span>}
                    </span>
                    <span className="session-meta">
                      IP {s.ipAddress} · signed in {new Date(s.createdAt).toLocaleString()} · last active{' '}
                      {new Date(s.lastUsedAt).toLocaleString()}
                    </span>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={revokingId === s.id}
                    onClick={() => revokeOne(s)}
                  >
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
            <div className="session-actions">
              <Button variant="danger" size="sm" onClick={revokeAll}>
                Sign out everywhere
              </Button>
            </div>
          </>
        )}
      </Card>

      <Card className="security-card">
        <Card.Header
          title="Change Password"
          subtitle="At least 10 characters with upper, lower, digit and special. Changing it signs you out of every device."
        />
        <form className="security-pw-form" onSubmit={handlePasswordChange}>
          {pwError && (
            <div className="security-pw-error" role="alert">
              {pwError}
            </div>
          )}
          <Input
            label="Current password"
            id="sec-current"
            type="password"
            value={pwForm.currentPassword}
            onChange={(e) => setPwForm((f) => ({ ...f, currentPassword: e.target.value }))}
            disabled={pwSaving}
            autoComplete="current-password"
          />
          <Input
            label="New password"
            id="sec-new"
            type="password"
            value={pwForm.newPassword}
            onChange={(e) => setPwForm((f) => ({ ...f, newPassword: e.target.value }))}
            disabled={pwSaving}
            autoComplete="new-password"
          />
          <Input
            label="Confirm new password"
            id="sec-confirm"
            type="password"
            value={pwForm.confirm}
            onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
            disabled={pwSaving}
            autoComplete="new-password"
          />
          <div className="security-otp-row">
            <Input
              label="Email code"
              id="sec-otp"
              inputMode="numeric"
              placeholder="6-digit code"
              value={pwForm.otp}
              onChange={(e) => setPwForm((f) => ({ ...f, otp: sanitizeOtp(e.target.value) }))}
              maxLength={OTP_LENGTH}
              disabled={pwSaving}
              autoComplete="one-time-code"
              helperText={otpSent ? 'Sent — check your inbox. It expires in 10 minutes.' : undefined}
            />
            <Button
              type="button"
              variant="secondary"
              onClick={sendPwOtp}
              loading={otpSending}
              disabled={pwSaving}
            >
              {otpSent ? 'Resend code' : 'Send code'}
            </Button>
          </div>
          <div>
            <Button
              type="submit"
              variant="primary"
              loading={pwSaving}
              disabled={
                !pwForm.currentPassword ||
                !pwForm.newPassword ||
                !pwForm.confirm ||
                !isCompleteOtp(pwForm.otp)
              }
            >
              Change Password
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

export default SecurityPanel;
